import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom, timeout } from 'rxjs';
import { environment } from 'src/environments/environment';

/** WhatsApp de soporte. El mensaje con los datos del cliente lo arma `asistente-operador.rules`. */
export const WHATSAPP_SOPORTE = '56965474761';
export const MENSAJE_SOPORTE = 'Hola, necesito asistencia';

export type ConsultaAsistente =
  | 'SALDO'
  | 'ULTIMA_OPERACION'
  | 'OPERACIONES'
  | 'FONDEOS'
  | 'ULTIMO_FONDEO'
  | 'TASA';

export interface MontoAsistente {
  moneda: string;
  monto: number;
}

export interface OperacionAsistente {
  numero: number;
  titulo: string;
  origen: MontoAsistente | null;
  destino: MontoAsistente | null;
  fecha: string | null;
  fechaTermino: string | null;
  estado:
    | 'COMPLETADA'
    | 'EN_PROCESO'
    | 'ESPERANDO_PAGO'
    | 'RECHAZADA'
    | 'CANCELADA'
    | 'ANULADA';
  estadoTexto: string;
}

export interface FondeoAsistente {
  referencia: string;
  medio: string;
  monto: MontoAsistente | null;
  fecha: string | null;
  estado: 'ACREDITADO' | 'EN_PROCESO' | 'EN_REVISION' | 'RECHAZADO';
  estadoTexto: string;
}

export interface TasaAsistente {
  titulo: string;
  origen: 'CLP' | 'VES';
  destino: 'VES' | 'CLP';
  /** Cómo leer `tasa`: VES por 1 CLP, o CLP por 1 VES. */
  unidad: 'VES_POR_CLP' | 'CLP_POR_VES';
  /** Montos en CLP; `tasa` según `unidad`, o `null` si ese tramo no está vigente. */
  tramos: { desde: number; hasta: number; tasa: number | null }[];
  disponible: boolean;
}

export interface RespuestaAsistente {
  tipo: ConsultaAsistente;
  saldos?: MontoAsistente[];
  consultadoEn?: string;
  operaciones?: OperacionAsistente[];
  fondeos?: FondeoAsistente[];
  tasa?: TasaAsistente;
}

const MENSAJE_ERROR =
  'No pudimos cargar esta información. Intenta de nuevo o habla con un operador.';

/**
 * Asistente de botones del cliente (fase 1, sin IA).
 *
 * EL ESTADO SE PIDE AL CARGAR, NO AL TOCAR EL BOTÓN. Si el botón esperara la
 * respuesta del servidor antes de decidir, y la respuesta fuera "apagado",
 * habría que abrir WhatsApp después de un `await`, y Safari en iPhone bloquea
 * las ventanas que no nacen directo de un toque. Por eso `cargarEstado()` corre
 * cuando arranca el navbar y el toque decide en el acto con lo que haya:
 * encendido abre el panel; apagado, sin respuesta todavía o con error, abre
 * WhatsApp como siempre.
 *
 * Todas las llamadas van con `X-Skip-Error-Interceptor`: un error del
 * asistente se muestra dentro del panel y nunca dispara los modales globales.
 */
@Injectable({ providedIn: 'root' })
export class AsistenteClienteService {
  private readonly base = `${environment.apiNest}asistente-cliente`;
  private readonly headers = new HttpHeaders({ 'X-Skip-Error-Interceptor': 'true' });

  private activoFlag = false;
  private nombreCliente: string | null = null;
  private cargando: Promise<void> | null = null;
  /**
   * Generación de la sesión. `reiniciar()` la sube, y una respuesta de
   * `/estado` que llega de una generación anterior se descarta: así un cambio
   * de cuenta no hereda el saludo ni el interruptor de la cuenta anterior.
   */
  private generacion = 0;

  constructor(private readonly http: HttpClient) {}

  get activo(): boolean {
    return this.activoFlag;
  }

  get nombre(): string | null {
    return this.nombreCliente;
  }

  /** Nunca lanza: ante cualquier falla el asistente queda apagado. */
  cargarEstado(): Promise<void> {
    if (this.cargando) return this.cargando;
    const generacion = this.generacion;
    const carga = (async () => {
      try {
        const r = await firstValueFrom(
          this.http
            .get<{ activo?: unknown; nombre?: unknown }>(`${this.base}/estado`, {
              headers: this.headers,
            })
            .pipe(timeout(8000)),
        );
        if (generacion !== this.generacion) return;
        this.activoFlag = r?.activo === true;
        this.nombreCliente = typeof r?.nombre === 'string' ? r.nombre : null;
      } catch {
        if (generacion !== this.generacion) return;
        this.activoFlag = false;
        this.nombreCliente = null;
      } finally {
        // Si hubo `reiniciar()` en el medio, `cargando` ya es de la sesión nueva.
        if (generacion === this.generacion) this.cargando = null;
      }
    })();
    this.cargando = carga;
    return carga;
  }

  /** Olvida el estado (al cerrar sesión, para no arrastrarlo a otra cuenta). */
  reiniciar(): void {
    this.generacion++;
    this.cargando = null;
    this.activoFlag = false;
    this.nombreCliente = null;
  }

  async consultar(tipo: ConsultaAsistente, cantidad?: 5 | 10): Promise<RespuestaAsistente> {
    let params = new HttpParams().set('tipo', tipo);
    if (cantidad) params = params.set('cantidad', String(cantidad));
    return firstValueFrom(
      this.http
        .get<RespuestaAsistente>(`${this.base}/consulta`, { headers: this.headers, params })
        .pipe(timeout(15000)),
    );
  }

  /**
   * Texto para mostrar en el panel cuando una consulta falla. Sólo se usa el
   * mensaje del servidor en los casos que el servidor redacta para el cliente
   * (429, 503, 404); cualquier otro error muestra el genérico.
   */
  mensajeDeError(error: unknown): string {
    if (error instanceof HttpErrorResponse && [404, 429, 503].includes(error.status)) {
      const m = error.error?.message;
      if (typeof m === 'string' && m.trim() && m.length <= 200) return m;
    }
    return MENSAJE_ERROR;
  }

  /**
   * Abre el WhatsApp de soporte. Se llama directo desde un toque del cliente,
   * nunca después de un `await`, para que el navegador no la bloquee.
   */
  abrirWhatsApp(texto: string = MENSAJE_SOPORTE): void {
    const url = `https://wa.me/${WHATSAPP_SOPORTE}?text=${encodeURIComponent(texto)}`;
    window.open(url, '_blank', 'noopener');
  }
}
